import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  User,
  KeyRound,
  LogOut,
  FolderKanban,
  PanelLeftOpen,
  PanelLeftClose,
  Search,
  Sparkles,
} from 'lucide-react';

import { useAuth } from '@context/AuthContext';
import { useUIStore } from '@/store/ui.store';
import { cn } from '@utils/cn';
import NotificationPanel from '@components/layout/NotificationPanel';
import CommandPalette from '@components/layout/CommandPalette';
import { useAtajoComandos } from '@hooks/useAtajoComandos';
import AIAssistant from '@components/layout/AIAssistant';
import { FS, PASO, useTema } from '@components/M02/casaTema';
import { tituloDeRuta, MENU_ANCHO } from '@/utils/navegacion';

// ─── Título de la cabecera ────────────────────────────────────
// I-251: sale de la lista única (`utils/navegacion.ts`), no de un mapa propio.
export function getCurrentTitle(pathname: string): string {
  return tituloDeRuta(pathname);
}

const SIDEBAR_EXPANDED  = MENU_ANCHO.abierto;
const SIDEBAR_COLLAPSED = MENU_ANCHO.colapsado;

// ─── Topbar ───────────────────────────────────────────────────
const Topbar: React.FC = () => {
  const { usuario, logout } = useAuth();
  const { sidebarAbierto, toggleSidebar, noLeidas, aiAssistantOpen, toggleAIAssistant } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();
  const T = useTema();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const title = getCurrentTitle(location.pathname);
  const sidebarWidth = sidebarAbierto ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  const iniciales = usuario?.nombre
    ? usuario.nombre.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : 'RM';

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [userMenuOpen]);

  // ⌘K global — la definicion vive en el hook, compartida con la casa del proyecto.
  useAtajoComandos(useCallback(() => setCmdOpen((v) => !v), []));

  return (
    <>
    <header
      className="fixed z-[45] flex items-center gap-3 px-4"
      style={{
        top: 12,
        right: 12,
        left: sidebarWidth + 20,
        height: '64px',
        borderRadius: 14,
        background: T.panel,
        backdropFilter: 'blur(24px) saturate(160%)',
        WebkitBackdropFilter: 'blur(24px) saturate(160%)',
        border: `1px solid ${T.line}`,
        boxShadow: T.shadow,
        transition: 'left 220ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* ── Toggle sidebar ────────────────────────────────── */}
      <button
        onClick={toggleSidebar}
        className="flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
        style={{ width: '34px', height: '34px', color: T.fg3 }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
          (e.currentTarget as HTMLElement).style.color = T.fg2;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = '';
          (e.currentTarget as HTMLElement).style.color = T.fg3;
        }}
        title={sidebarAbierto ? 'Colapsar menú' : 'Expandir menú'}
      >
        {sidebarAbierto
          ? <PanelLeftClose size={17} />
          : <PanelLeftOpen size={17} />
        }
      </button>

      {/* ── Separador ─────────────────────────────────────── */}
      <div style={{ width: '1px', height: '18px', backgroundColor: T.line, flexShrink: 0 }} />

      {/* ── Título ────────────────────────────────────────── */}
      <h1 className="flex-1 truncate font-semibold" style={{ color: T.fg, fontSize: PASO.s15 }}>
        {title}
      </h1>

      {/* ── Buscador global → abre CommandPalette ─────────── */}
      <button
        onClick={() => setCmdOpen(true)}
        className="hidden md:flex items-center gap-2 rounded-xl text-sm transition-colors"
        style={{
          background: T.panel,
          border: `1px solid ${T.line}`,
          color: T.fg3,
          padding: '6px 13px',
          minWidth: '200px',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = T.raise;
          (e.currentTarget as HTMLElement).style.borderColor = T.inputLine;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = T.panel;
          (e.currentTarget as HTMLElement).style.borderColor = T.line;
        }}
      >
        <Search size={13} />
        <span className="text-xs flex-1 text-left">Buscar o ir a...</span>
        <span
          className="rounded text-[10px]"
          style={{
            background: T.panelSolid, color: T.fg3, padding: '1px 6px',
            border: `1px solid ${T.line}`,
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
          }}
        >
          ⌘K
        </span>
      </button>

      {/* ── Asistente IA ─────────────────────────────────── */}
      <button
        onClick={toggleAIAssistant}
        className="flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
        style={{
          width: '34px', height: '34px',
          color: aiAssistantOpen ? T.estados.cfo : T.fg3,
          background: aiAssistantOpen ? T.chip : 'transparent',
          border: aiAssistantOpen ? `1px solid ${T.line}` : '1px solid transparent',
        }}
        onMouseEnter={(e) => {
          if (!aiAssistantOpen) {
            (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
            (e.currentTarget as HTMLElement).style.color = T.estados.cfo;
          }
        }}
        onMouseLeave={(e) => {
          if (!aiAssistantOpen) {
            (e.currentTarget as HTMLElement).style.backgroundColor = '';
            (e.currentTarget as HTMLElement).style.color = T.fg3;
          }
        }}
        title="Asistente IA"
        aria-label="Asistente IA"
      >
        <Sparkles size={16} />
      </button>

      {/* ── Proyecto activo ───────────────────────────────── */}
      {usuario && usuario.proyectos.length > 0 && (
        <button
          className="hidden lg:flex items-center gap-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
          style={{
            background: T.panel,
            color: T.fg2,
            border: `1px solid ${T.line}`,
            padding: '6px 11px',
          }}
          onClick={() => navigate('/proyectos')}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = T.raise;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = T.panel;
          }}
        >
          <FolderKanban size={13} />
          {usuario.proyectos.length} proyecto{usuario.proyectos.length !== 1 ? 's' : ''}
        </button>
      )}

      {/* ── Notificaciones ────────────────────────────────── */}
      <button
        onClick={() => setNotifOpen(true)}
        className="relative flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
        style={{
          width: '34px', height: '34px', color: T.fg2,
          background: T.panel,
          border: `1px solid ${T.line}`,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = T.raise;
          (e.currentTarget as HTMLElement).style.color = T.fg2;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = T.panel;
          (e.currentTarget as HTMLElement).style.color = T.fg2;
        }}
        aria-label="Notificaciones"
      >
        <Bell size={16} />
        {noLeidas > 0 && (
          <span
            className="absolute flex items-center justify-center font-bold"
            style={{
              top: '5px', right: '5px',
              minWidth: '14px', height: '14px',
              backgroundColor: T.danger, color: T.dangerFg,
              borderRadius: '50%',
              fontSize: PASO.s10,
              border: `1.5px solid ${T.panelSolid}`,
              padding: '0 2px',
            }}
          >
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}
      </button>

      {/* ── Avatar + menú usuario ─────────────────────────── */}
      <div className="relative flex-shrink-0" ref={userMenuRef}>
        <button
          onClick={() => setUserMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded-xl transition-colors"
          style={{
            padding: '5px 10px 5px 6px',
            background: userMenuOpen ? T.raise : T.panel,
            border: `1px solid ${T.line}`,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = T.raise;
          }}
          onMouseLeave={(e) => {
            if (!userMenuOpen) (e.currentTarget as HTMLElement).style.background = T.panel;
          }}
        >
          <div
            className="flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
            style={{ width: '26px', height: '26px', background: T.chip, color: T.fg2 }}
          >
            {iniciales}
          </div>
          <span
            className="max-w-[90px] truncate font-medium hidden sm:block"
            style={{ color: T.fg, fontSize: PASO.s12 }}
          >
            {usuario?.nombre?.split(' ')[0] ?? 'Usuario'}
          </span>
          <ChevronDown
            size={12}
            style={{ color: T.fg3 }}
            className={cn('transition-transform', userMenuOpen && 'rotate-180')}
          />
        </button>

        {/* Dropdown */}
        {userMenuOpen && (
          <div
            className="absolute right-0 mt-1.5 py-1 rounded-xl shadow-lg animate-scale-in z-50"
            style={{
              width: '200px', top: '100%',
              background: T.panelSolid,
              backdropFilter: 'blur(24px)',
              border: `1px solid ${T.line}`,
              boxShadow: T.shadow,
            }}
          >
            <div className="px-3 py-2.5" style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
              <p className="font-semibold truncate" style={{ color: T.fg, fontSize: PASO.s13 }}>
                {usuario?.nombre ?? 'Usuario'}
              </p>
              <p className="truncate mt-0.5" style={{ color: T.fg3, fontSize: FS.ayuda }}>{usuario?.email}</p>
            </div>

            <div className="py-1">
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 transition-colors"
                style={{ fontSize: FS.menuItem, color: T.fg2 }}
                onClick={() => { setUserMenuOpen(false); navigate('/configuracion'); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.chip; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <User size={13} style={{ color: T.fg3 }} />
                Mi perfil
              </button>
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 transition-colors"
                style={{ fontSize: FS.menuItem, color: T.fg2 }}
                onClick={() => { setUserMenuOpen(false); navigate('/configuracion'); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.chip; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <KeyRound size={13} style={{ color: T.fg3 }} />
                Cambiar contraseña
              </button>
            </div>

            <div className="py-1" style={{ borderTop: `1px solid ${T.lineSoft}` }}>
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 transition-colors"
                style={{ fontSize: FS.menuItem, color: T.danger }}
                onClick={async () => { setUserMenuOpen(false); await logout(); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.dangerSoft; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <LogOut size={13} />
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </header>

    <NotificationPanel isOpen={notifOpen} onClose={() => setNotifOpen(false)} />
    <CommandPalette isOpen={cmdOpen} onClose={() => setCmdOpen(false)} />
    <AIAssistant />
    </>
  );
};

export default Topbar;
